// src/components/store/sections/weekly-banner/Variant5YellowChevron.tsx
"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { WeeklyBannerProps } from "./types";

export default function Variant5YellowChevron({ banner, subdomain }: WeeklyBannerProps) {
  const imageUrl = banner?.image
    ? banner.image.startsWith("http") || banner.image.startsWith("data:")
      ? banner.image
      : `http://${subdomain ? `${subdomain}.` : ""}localhost:8000${banner.image.startsWith("/") ? "" : "/"}${banner.image}`
    : "https://images.unsplash.com/photo-1573855619003-97b4799dcd8b?w=900&auto=format&fit=crop&q=80";

  return (
    <section className="max-w-7xl mx-auto px-3 sm:px-6 select-none font-sans">
      <div className="relative w-full rounded-[24px] sm:rounded-[36px] bg-[#f5b700] overflow-hidden shadow-2xl border border-amber-300 flex flex-col md:flex-row items-stretch min-h-[340px] sm:min-h-[380px]">
        {/* 🌟 ১. বাম পাশ: স্কাই ব্লু ব্যাকড্রপ + মডেল ইমেজ */}
        <div className="relative w-full md:w-[48%] h-64 sm:h-80 md:h-auto bg-[#8cbfe4] overflow-hidden shrink-0 flex items-center justify-center">
          <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-60 z-10" viewBox="0 0 400 300">
            <polyline points="20,280 80,240 120,290 20,280" fill="none" stroke="#ffffff" strokeWidth="1.2" />
            <circle cx="20" cy="280" r="3" fill="#ffffff" />
            <circle cx="80" cy="240" r="3" fill="#ffffff" />
            <circle cx="120" cy="290" r="3" fill="#ffffff" />
          </svg>

          <div className="relative w-full h-full">
            <Image
              src={imageUrl}
              alt={banner?.title || "Cyber Monday"}
              fill
              unoptimized
              className="object-cover object-center md:object-right-bottom"
            />
          </div>

          {/* শেভরন ডিভাইডার */}
          <div className="hidden md:block absolute -right-[1px] top-0 bottom-0 w-24 pointer-events-none z-20">
            <svg viewBox="0 0 100 300" preserveAspectRatio="none" className="w-full h-full">
              <polygon points="0,0 35,150 0,300 30,300 65,150 30,0" fill="#2d3436" opacity="0.6" />
              <polygon points="30,0 65,150 30,300 100,300 100,0" fill="#f5b700" />
              <polygon points="25,0 60,150 25,300 55,300 90,150 55,0" fill="#111111" />
            </svg>
          </div>

          {/* লাল হেক্সাগন ডিসকাউন্ট ব্যাজ */}
          <div
            style={{
              clipPath: "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
            }}
            className="absolute bottom-4 md:bottom-8 right-4 md:right-8 z-30 w-24 sm:w-28 h-28 sm:h-32 bg-[#e81123] flex flex-col items-center justify-center text-white shadow-2xl p-1"
          >
            <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider">UP TO</span>
            <span className="text-xl sm:text-2xl font-black leading-none my-0.5">
              {banner?.badge_title?.replace(/[^0-9%]/g, "") || "20%"}
            </span>
            <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-wider">OFF</span>
          </div>
        </div>

        {/* 🌟 ২. ডান পাশ: ভাইব্র্যান্ট ইয়েলো ব্যাকড্রপ ও কনটেন্ট */}
        <div className="relative flex-1 p-6 sm:p-10 md:p-12 flex flex-col justify-between z-10 bg-[#f5b700]">
          <div className="space-y-1 text-center md:text-right mt-2 sm:mt-0">
            <span className="text-xs sm:text-sm font-black tracking-[0.2em] text-slate-900 uppercase block">
              SPECIAL OFFER
            </span>

            <h2 className="text-4xl sm:text-6xl lg:text-7xl font-black italic tracking-tight uppercase leading-none">
              <span className="block text-[#e81123] drop-shadow-[0_2px_8px_rgba(232,17,35,0.3)]">
                {banner?.title ? banner.title.split(" ")[0] : "CYBER"}
              </span>
              <span className="block text-[#111111]">
                {banner?.title && banner.title.split(" ").length > 1
                  ? banner.title.split(" ").slice(1).join(" ")
                  : "MONDAY"}
              </span>
            </h2>

            <p className="text-xs sm:text-base font-black tracking-[0.2em] text-slate-900 uppercase pt-1">
              {banner?.subtitle || "ALL ITEMS"}
            </p>
          </div>

          <div className="pt-6 mt-4 flex flex-col sm:flex-row items-center justify-end gap-5">
            {/* ইনলাইন সোশ্যাল আইকনস */}
            <div className="flex items-center gap-3 order-2 sm:order-1 text-slate-900">
              <span className="w-5 h-5 flex items-center justify-center cursor-pointer hover:scale-125 transition-transform" title="Instagram">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </span>
              <span className="w-5 h-5 flex items-center justify-center cursor-pointer hover:scale-125 transition-transform" title="Facebook">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </span>
              <span className="w-5 h-5 flex items-center justify-center cursor-pointer hover:scale-125 transition-transform" title="Twitter / X">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </span>
            </div>

            <Link
              href={banner?.target_url || "/products"}
              className="order-1 sm:order-2 px-8 py-3 rounded-full bg-[#e81123] hover:bg-[#c90d1d] text-white font-black text-xs uppercase tracking-wider inline-flex items-center gap-2 shadow-lg shadow-red-600/30 active:scale-95 transition-all cursor-pointer"
            >
              <span>{banner?.button_text || "SHOP NOW"}</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export { Variant5YellowChevron };